import { createGuest, updateGuest } from './../graphql/mutations';
import {
  UpdateGuestInput,
  Guest,
  CreateGuestInput,
  CreateTimelineInput,
} from './../models/GQL_API';
import { useDispatch, useSelector } from 'react-redux';
import {
  setListing,
  setSelected,
  setNextToken,
  setPrevNextToken,
} from '../store/ducks/guest';
import {
  guestBulkTrashVariables,
  guestGetVariables,
  guestUpdateVariables,
  CreateVariables,
  // ListingVariables,
  Option,
  HeadCell,
  guestListingVariables,
} from '../models/app';
import { deleteGuest } from '../graphql/mutations';
import useApp from './useApp';
import useTimeline from './useTimeline';
import {
  ByPhoneNumber,
  getGuest,
  listGuests,
  userByDeleted,
} from '../graphql/queries';
import { onCreateGuest } from '../graphql/subscriptions';
import { generateClient } from '@aws-amplify/api';
import { Pages, /* TimeLineForGuest, */ TimelineTypes } from '../constants/enums';
import {
  normalizePhone,
  phoneVariants,
  toAsciiDigits,
  toPhoneSearchText,
  validatePhone,
} from '../helpers/utils';
import { toDayRangeIso } from '../helpers/dateRange';
import { useParams } from 'react-router-dom';
import axios from 'axios';
// import { useEffect } from 'react';

const client = generateClient();
const useGuest = (listingName: string, singleName: string) => {
  let { slug } = useParams();
  const dispatch = useDispatch();
  const { showConfirm, showError, setSelectedUser } = useApp();
  const serverAddress = useSelector((state: any) => state.server.serverAddress);
  // const serverStatus = useSelector((state: any) => state.server.serverStatus);
  const { timelinesCreate } = useTimeline('timelines', 'timelines');

  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]['nextToken'],
  );
  const prevNextToken = useSelector(
    (state: any) => state[`${listingName}`]['prevNextToken'],
  );
  const guestListing = useSelector(
    (state: any) => state[`${listingName}`]['listing'],
  );
  const session = useSelector((state: any) => state.app.session);
  const newFirstFetch = useSelector((state: any) => state.app.firstFetch);
  // useEffect(() => {
  //   if (newFirstFetch) {
  //     dispatch(setListing([]));
  //     dispatch(setNextToken(null));
  //   }
  // }, [newFirstFetch]);
  async function fetch(params: guestListingVariables) {
    try {
      const { searchText /*, limit */, generalFilters } = params;
      const filter: any = {};
      filter.or = [];
      filter.and = [];
      // Text search: guest names are stored lowercased (app + admin create /
      // update), and `name` is the sort key of the userByDeleted index, so a
      // name prefix is a key condition instead of a filter over every guest.
      // Digits keep the phone filter. Arabic-Indic / Persian digits are
      // converted first (they are in the Arabic letter range below).
      const trimmedSearch = toAsciiDigits(searchText).trim();
      const isNameSearch = /[a-zA-Z\u0600-\u06FF]/.test(trimmedSearch);
      if (trimmedSearch.length > 0 && !isNameSearch) {
        filter.or.push({
          phone_number: { contains: toPhoneSearchText(trimmedSearch) },
        });
      }
      // Whole local days; the caller's generalFilters are not modified.
      const createdAtRange = toDayRangeIso(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );
      if (createdAtRange) {
        filter.and.push({ createdAt: { between: createdAtRange } });
      }

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      const groupsList: any = await client.graphql<Guest>({
        query: userByDeleted,
        // variables: { filter, limit, nextToken: nextToken, deleted: '0' },
        variables: {
          filter,
          limit: 1000,
          nextToken: nextToken,
          deleted: '0',
          ...(isNameSearch
            ? { name: { beginsWith: trimmedSearch.toLowerCase() } }
            : {}),
        },
        authMode: 'userPool',
      });
      const currentNextToken = groupsList.data.userByDeleted.nextToken;
      const responseListing = groupsList.data.userByDeleted.items;
      // Filters changed while this page was loading: drop the old response.
      if (params.isStale?.()) return [];

      let listing = [...guestListing, ...responseListing];
      // if (responseListing?.length > 0) {
      dispatch(setListing(listing));
      // }
      dispatch(setPrevNextToken(nextToken));
      dispatch(setNextToken(currentNextToken));
      return listing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function fetchAll(params: guestListingVariables) {
    try {
      const { searchText /*, limit */, generalFilters } = params;
      let allListing: any[] = [];
      const filter: any = {
        deleted: { eq: '0' },
      };
      filter.or = [];
      filter.and = [];
      const asciiSearch = toAsciiDigits(searchText).trim();
      if (asciiSearch.length > 0) {
        filter.or.push({ name: { contains: asciiSearch.toLowerCase() } });
        filter.or.push({
          phone_number: { contains: toPhoneSearchText(asciiSearch) },
        });
      }
      const createdAtRange = toDayRangeIso(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );
      if (createdAtRange) {
        filter.and.push({ createdAt: { between: createdAtRange } });
      }

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      const groupsList: any = await client.graphql<Guest>({
        query: listGuests,
        variables: { filter, limit: 1000 },
        authMode: 'userPool',
      });
      let currentNextToken = groupsList.data.listGuests.nextToken;
      allListing = groupsList.data.listGuests.items;
      while (currentNextToken) {
        const groupsListNew: any = await client.graphql<Guest>({
          query: listGuests,
          variables: { filter, limit: 1000, nextToken: currentNextToken },
          authMode: 'userPool',
        });
        allListing = [...allListing, ...groupsListNew.data.listGuests.items];
        currentNextToken = groupsListNew.data.listGuests.nextToken;
      }
      return allListing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function fetchByPhoneNumber(params: any) {
    const { phone_number } = params;
    const filter: any = {
      deleted: { eq: '0' },
    };
    try {
      // Read every stored format of the number (older rows aren't normalized).
      const results = await Promise.all(
        phoneVariants(phone_number).map((variant) =>
          client.graphql({
            query: ByPhoneNumber,
            variables: { filter, phone_number: variant, limit: 1000 },
            authMode: 'userPool',
          }),
        ),
      );
      const items: any[] = results.flatMap(
        (listing: any) => listing?.data?.ByPhoneNumber?.items ?? [],
      );
      // Prefer the registered account when the same person has several rows.
      return (
        items.find((g) => g?.group === 'Cognito' && g?.sub && !g?.needPassword) ??
        items[0]
      );
    } catch (err) {
      throw err;
    }
  }

  async function fetchGuestOffline(phoneNumber: string) {
    try {
      const res = await axios.get(
        serverAddress + '/booking/user?phone=' + phoneNumber,
      );
      const data = res.data.data.data;
      return data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        throw new Error("This Guest Doesn't exist");
      } else {
        throw new Error(err?.response?.data?.message) ?? err;
      }
    }
  }

  async function fetchConnections(params: guestListingVariables) {
    const { searchText /*, startIndex, limit */, connectionsIDs } = params;

    try {
      const ids = Array.from(
        new Set((connectionsIDs ?? []).filter((id): id is string => !!id)),
      );
      if (ids.length === 0) return [];

      // Direct getGuest reads by id (in parallel chunks). This used to scan
      // listGuests with an OR of ids and limit 1000, which only looked at
      // the first 1000 guests of the table and missed most connections.
      const CHUNK_SIZE = 25;
      const guests: Guest[] = [];
      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CHUNK_SIZE);
        const results = await Promise.all(
          chunk.map((id) =>
            client
              .graphql({
                query: getGuest,
                variables: { id },
                authMode: 'userPool',
              })
              .then((res: any) => res?.data?.getGuest as Guest | null)
              .catch(() => null),
          ),
        );
        for (const guest of results) {
          if (guest) guests.push(guest);
        }
      }

      const search = (searchText ?? '').toLowerCase();
      return guests.filter(
        (guest: any) =>
          guest.deleted === '0' &&
          !guest._deleted &&
          (!search || String(guest.name ?? '').toLowerCase().includes(search)),
      );
    } catch (err: Error | any) {
      showError(err);
    }
  }

  const getName = (params: guestGetVariables) => {
    const { id, listing } = params;
    if (listing.length > 0) {
      const model = listing.find((model: Guest) => model.id === id);
      return model ? model.name : '';
    }
    return '';
  };
  async function get(params: guestGetVariables) {
    try {
      const { id, listing /*, getNew */ } = params;
      let single: Guest | undefined;
      if (listing?.length /* && !getNew */) {
        single = listing.find((resource: any) => resource.id === id);
      }
      if (single === undefined) {
        const listing: any = await client.graphql<Guest>({
          query: getGuest,
          variables: { id },
          authMode: 'userPool',
        });
        single = listing.data.getGuest;
      }
      return single;
    } catch (err) {
      showError(err);
    }
  }
  async function create(params: CreateVariables) {
    try {
      const { userID, userName, data } = params;
      const createInput: CreateGuestInput = {
        // name: data.name.toLowerCase(),
        // name: data.name,
        guestGroupID: data.guestGroupID,
        deleted: '0',
        createdAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
        // last_attended_event is not set by admin create (it is attendance data)
      };
      // Name is the sort key of the phone/email indexes: a guest without a name
      // can't be found by phone later, and the website would create a duplicate.
      if (!data.name?.trim()) {
        throw new Error('Guest name is required');
      }
      createInput.name = data.name.trim().toLowerCase();
      if (data.username) createInput.username = data.username;
      if (data.email) createInput.email = data.email.trim().toLowerCase();
      if (data.phone_number)
        createInput.phone_number = normalizePhone(data.phone_number);
      if (data.guest_avatar) createInput.guest_avatar = data.guest_avatar;
      if (data.avg_spend) createInput.avg_spend = data.avg_spend;
      if (data.address) createInput.address = data.address;
      if (data.gender) createInput.gender = data.gender;
      if (data.birthdate) createInput.birthdate = data.birthdate;
      if (data.avg_ticket_type)
        createInput.avg_ticket_type = data.avg_ticket_type;
      if (data.isVerified) createInput.isVerified = data.isVerified;
      if (data.numberOfTickets)
        createInput.numberOfTickets = data.numberOfTickets;
      if (data.nationality) createInput.nationality = data.nationality;
      // if (data.connections) createInput.connections = data.connections;
      // if (data.last_attended_event) createInput.last_attended_event = data.last_attended_event;

      if (data.phone_number) {
        if (data.phone_number && !validatePhone(data.phone_number)) {
          throw new Error('Phone number is not valid');
        }
        const params = {
          phone_number: data.phone_number,
        };
        let userExists = await fetchByPhoneNumber(params);
        if (userExists) {
          throw new Error('Phone number already exists');
        }
      }

      const res = await client.graphql({
        query: createGuest,
        variables: { input: createInput },
        authMode: 'userPool',
      });
      showConfirm(`New ${singleName} has been created successfully`);
      return res.data.createGuest;
    } catch (err) {
      showError(err);
    }
  }
  async function update(params: guestUpdateVariables) {
    try {
      const { data /*, getNew */ } = params;
      const original: any = await get({ ...params, getNew: true });
      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }

      const updateInput: UpdateGuestInput = {
        id: original.id,
        name: data.name
          ? data.name.toLowerCase()
          : original!.name.toLowerCase(),
        username: data.username ? data.username : original!.username,
        email: data.email ? data.email : original!.email,
        // Normalize only a changed number; an unchanged stored value is left as is.
        phone_number:
          data.phone_number && data.phone_number !== original!.phone_number
            ? normalizePhone(data.phone_number)
            : original!.phone_number,
        guestGroupID: data.guestGroupID
          ? data.guestGroupID
          : original!.guestGroupID,
        guestGroupName: data.guestGroupName
          ? data.guestGroupName
          : original!.guestGroupName,
        guest_avatar: data.guest_avatar
          ? data.guest_avatar
          : original!.guest_avatar,
        avg_spend: data.avg_spend ? data.avg_spend : original!.avg_spend,
        avg_ticket_type: data.avg_ticket_type
          ? data.avg_ticket_type
          : original!.avg_ticket_type,
        numberOfTickets: data.numberOfTickets
          ? data.numberOfTickets
          : original!.numberOfTickets,
        connections: data.connections
          ? data.connections
          : original!.connections,
        // if (data.isVerified) createInput.isVerified = data.isVerified;
        isVerified:
          data.isVerified !== null && data.isVerified !== undefined
            ? data.isVerified
            : original!.isVerified,
        isBlocked:
          data.isBlocked !== null && data.isBlocked !== undefined
            ? data.isBlocked
            : original!.isBlocked,
        // isVerified: data.isVerified ? data.isVerified : original!.isVerified,
        // last_attended_event: data.last_attended_event ? data.last_attended_event : original!.last_attended_event,
        // last_attended_event: left untouched by admin edits (attendance data)
        gender: data.gender ? data.gender : original!.gender,
        deleted: original!.deleted,
        address: data.address ? data.address : original!.address,
        birthdate: data.birthdate ? data.birthdate : original!.birthdate,
        nationality: data.nationality
          ? data.nationality
          : original!.nationality,
        flags: data.flags ? data.flags : original!.flags,
        createdAt: original!.createdAt,
        _version: original._version,
      };

      if (data.phone_number) {
        if (data.phone_number && !validatePhone(data.phone_number)) {
          throw new Error('Phone number is not valid');
        }
        const params = {
          phone_number: data.phone_number,
        };
        let userExists = await fetchByPhoneNumber(params);
        if (userExists && userExists.id !== original.id) {
          throw new Error('Phone number already exists');
        }
      }

      let updatedGuest = await client.graphql({
        query: updateGuest,
        variables: { input: updateInput },
        authMode: 'userPool',
      });

      if (slug === Pages.GUESTS) {
        setSelectedUser(updatedGuest.data.updateGuest);
      }

      const createInput: CreateTimelineInput = {
        ...data,
        customerId: original.id,
        address: data.address,
        resource: original!,
        type: TimelineTypes.INFO,
        timelinesListing: data.timelinesListing,
        // statusesListing: data.statusesListing,
        // timeSlotsListing: data.timeSlotsListing,
      };

      const timelineParams: CreateVariables = {
        userID: session.sub,
        userName: session.name,
        data: createInput,
      };

      await timelinesCreate(timelineParams);

      showConfirm(`${singleName} has been updated successfully`);
      return updatedGuest.data.updateGuest;
    } catch (err) {
      showError(err);
    }
  }
  async function trash(params: guestGetVariables) {
    try {
      const original: any = await get(params);
      const { id, listing } = params;
      if (original) {
        const updateInput: UpdateGuestInput = {
          id: original.id,
          deleted: '1',
          name: original.name,
          createdAt: original!.createdAt,
          _version: original._version,
        };
        await client.graphql<Guest>({
          query: updateGuest,
          variables: { input: updateInput },
          authMode: 'userPool',
        });
      }
      dispatch(setListing(listing.filter((model: any) => model.id !== id)));
      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }
  async function bulkTrash(params: guestBulkTrashVariables) {
    const { ids, listing } = params;
    ids.forEach(async (id: string) => {
      try {
        await trash({ id, listing });
      } catch (err: Error | any) {
        throw err;
      }
    });
    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));
    return `${ids.size} ${listingName} items has been moved to trash`;
  }
  async function remove(params: guestGetVariables) {
    try {
      const { id, listing } = params;
      await client.graphql<Guest>({
        query: deleteGuest,
        variables: { id: id },
        authMode: 'userPool',
      });
      dispatch(setListing(listing.filter((model: any) => model.id !== id)));
      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }
  async function exportAll(params: any) {
    try {
      let exportedData: Guest[] = [];
      const data: Guest[] = await fetchAll(params);
      for (let user of data!) {
        let row: Guest = { ...user };
        exportedData.push(row);
      }
      return exportedData;
    } catch (err: any) {
      showError(err);
    }
  }
  function options(listing: Guest[]) {
    const options: Option[] = [];
    for (let option of listing) {
      options.push({ label: option.name!, value: option.id });
    }
    return options;
  }
  const headCells: readonly HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'Guest',
    },
    {
      id: 'phone_number',
      numeric: false,
      disablePadding: false,
      label: 'Mobile No.',
    },
    {
      id: 'avg_spend',
      numeric: false,
      disablePadding: false,
      label: 'Avg. Spend',
    },
    {
      id: 'avg_ticket_type',
      numeric: false,
      disablePadding: false,
      label: 'Avg. Ticket Type',
    },
    {
      id: 'connections',
      numeric: false,
      disablePadding: false,
      label: 'Connections',
    },
    {
      id: 'last_attended_event',
      numeric: false,
      disablePadding: false,
      label: 'Last attended event',
    },
    // {
    //     id: 'createdBy',
    //     numeric: false,
    //     disablePadding: false,
    //     label: 'Created By',
    // },
    // {
    //     id: 'createdAt',
    //     numeric: false,
    //     disablePadding: false,
    //     label: 'Date',
    // },
    {
      id: 'actions',
      numeric: true,
      disablePadding: false,
      label: '',
    },
  ];
  const dataCells: readonly string[] = [
    'name',
    'phone_number',
    'avg_spend',
    'avg_ticket_type',
    'connections',
    'last_attended_event',
  ];
  const api: any = {};

  api[`${listingName}CreateSubscription`] = onCreateGuest;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByPhoneNumber`] = fetchByPhoneNumber;
  api[`${listingName}FetchGuestOfflineByPhone`] = fetchGuestOffline;
  api[`${listingName}FetchConnections`] = fetchConnections;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}ChangeListing`] = (listing: Guest[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}PrevNextToken`] = prevNextToken;
  api[`${listingName}Listing`] = guestListing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  return api;
};
export default useGuest;
